class CollectionCategoryCarousel extends HTMLElement {
  #slider = null;
  #controls = null;
  #progressTrack = null;
  #prevButton = null;
  #nextButton = null;
  #progressThumb = null;
  #progressBar = null;
  #slides = [];
  #scrollRaf = null;
  #isNavigating = false;
  #resizeObserver = null;
  #initRetryCount = 0;
  #onSectionLoad = null;

  connectedCallback() {
    this.#slider = this.querySelector('[data-collection-category-slider]');
    this.#controls = this.querySelector('[data-collection-category-controls]');
    this.#progressTrack = this.querySelector('[data-collection-category-progress-track]');
    this.#prevButton = this.querySelector('[data-collection-category-prev]');
    this.#nextButton = this.querySelector('[data-collection-category-next]');
    this.#progressThumb = this.querySelector('[data-collection-category-progress-thumb]');
    this.#progressBar = this.querySelector('[role="progressbar"]');
    this.#slides = Array.from(this.querySelectorAll('[data-collection-category-slide]'));

    if (!this.#slider || !this.#progressThumb) return;

    this.#prevButton?.addEventListener('click', () => this.#scrollByDirection('prev'));
    this.#nextButton?.addEventListener('click', () => this.#scrollByDirection('next'));
    this.#slider.addEventListener('scroll', this.#onSliderScroll.bind(this), { passive: true });

    this.#slider.addEventListener(
      'load',
      () => {
        this.#scheduleProgressUpdate();
      },
      true
    );

    this.#resizeObserver = new ResizeObserver(() => {
      this.#scheduleProgressUpdate();
      this.#updateControls();
    });
    this.#resizeObserver.observe(this.#slider);

    for (const slide of this.#slides) {
      this.#resizeObserver.observe(slide);
    }

    window.addEventListener('load', () => this.#scheduleProgressUpdate(), { once: true });

    this.#onSectionLoad = (event) => {
      if (!event.target.contains(this)) return;

      this.#initRetryCount = 0;
      this.#scheduleProgressUpdate();
      this.#updateControls();
    };
    document.addEventListener('shopify:section:load', this.#onSectionLoad);

    this.#scheduleProgressUpdate();
    this.#updateControls();
  }

  disconnectedCallback() {
    this.#resizeObserver?.disconnect();

    if (this.#onSectionLoad) {
      document.removeEventListener('shopify:section:load', this.#onSectionLoad);
    }
  }

  #scheduleProgressUpdate() {
    if (this.#scrollRaf) cancelAnimationFrame(this.#scrollRaf);

    this.#scrollRaf = requestAnimationFrame(() => {
      this.#updateProgress();

      if (!this.#hasValidMetrics() && this.#initRetryCount < 10) {
        this.#initRetryCount += 1;
        window.setTimeout(() => this.#scheduleProgressUpdate(), 100);
      }
    });
  }

  #hasValidMetrics() {
    if (!this.#slider) return false;

    const { scrollWidth, clientWidth } = this.#slider;
    return clientWidth > 0 && scrollWidth > 0;
  }

  #onSliderScroll() {
    if (this.#scrollRaf) cancelAnimationFrame(this.#scrollRaf);

    this.#scrollRaf = requestAnimationFrame(() => {
      this.#updateProgress();
      this.#updateControls();
    });
  }

  #scrollByDirection(direction) {
    const currentIndex = this.#getActiveSlideIndex();
    const nextIndex = direction === 'next' ? currentIndex + 1 : currentIndex - 1;
    this.#goToSlide(nextIndex);
  }

  #getActiveSlideIndex() {
    const scrollLeft = this.#slider.scrollLeft;
    let activeIndex = 0;
    let smallestDistance = Infinity;

    for (const [index, slide] of this.#slides.entries()) {
      const distance = Math.abs(slide.offsetLeft - scrollLeft);

      if (distance < smallestDistance) {
        smallestDistance = distance;
        activeIndex = index;
      }
    }

    return activeIndex;
  }

  #goToSlide(index) {
    const slide = this.#slides[index];
    if (!slide) return;

    this.#isNavigating = true;

    const scrollBehavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';

    this.#slider.scrollTo({
      left: slide.offsetLeft,
      behavior: scrollBehavior,
    });

    window.setTimeout(() => {
      this.#isNavigating = false;
      this.#updateProgress();
      this.#updateControls();
    }, scrollBehavior === 'smooth' ? 400 : 0);
  }

  #getMaxScroll() {
    return Math.max(0, this.#slider.scrollWidth - this.#slider.clientWidth);
  }

  #hasOverflow() {
    return this.#getMaxScroll() > 1;
  }

  #updateProgress() {
    if (!this.#progressThumb || !this.#slider) return;

    if (!this.#hasValidMetrics()) return;

    const { scrollLeft, scrollWidth, clientWidth } = this.#slider;
    const maxScroll = this.#getMaxScroll();

    if (!this.#hasOverflow()) {
      this.#setThumbPosition(100, 0);
      this.#progressBar?.setAttribute('aria-valuenow', '100');
      return;
    }

    const thumbWidthPercent = Math.max((clientWidth / scrollWidth) * 100, 8);
    const maxThumbTravel = 100 - thumbWidthPercent;
    const thumbLeftPercent = maxScroll > 0 ? (scrollLeft / maxScroll) * maxThumbTravel : 0;
    const scrollPercent = maxScroll > 0 ? Math.round((scrollLeft / maxScroll) * 100) : 0;

    this.#setThumbPosition(thumbWidthPercent, thumbLeftPercent);
    this.#progressBar?.setAttribute('aria-valuenow', String(scrollPercent));
  }

  #setThumbPosition(widthPercent, leftPercent) {
    const width = `${widthPercent}%`;
    const left = `${leftPercent}%`;

    this.#progressThumb.style.width = width;
    this.#progressThumb.style.left = left;
    this.#progressTrack?.style.setProperty('--collection-category-thumb-width', width);
    this.#progressTrack?.style.setProperty('--collection-category-thumb-left', left);
  }

  #updateControls() {
    const hasOverflow = this.#hasOverflow();

    this.#controls?.classList.toggle('collection-category-controls--hidden', !hasOverflow);
    this.classList.toggle('collection-category-carousel--scrollable', hasOverflow);

    if (!hasOverflow) {
      this.#prevButton?.setAttribute('disabled', 'disabled');
      this.#nextButton?.setAttribute('disabled', 'disabled');
      return;
    }

    const atStart = this.#slider.scrollLeft <= 1;
    const atEnd = this.#slider.scrollLeft >= this.#getMaxScroll() - 1;

    if (atStart) {
      this.#prevButton?.setAttribute('disabled', 'disabled');
    } else {
      this.#prevButton?.removeAttribute('disabled');
    }

    if (atEnd) {
      this.#nextButton?.setAttribute('disabled', 'disabled');
    } else {
      this.#nextButton?.removeAttribute('disabled');
    }
  }
}

customElements.define('collection-category-carousel', CollectionCategoryCarousel);
